import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AssetResponse, FormatTypeOption, VocabularyResponse } from '../../api';
import type { BriefingRow } from '../briefing/api';
import { assetTypesOf } from '../../constants/assetTypes';
import {
  CHANNEL_OPTIONS,
  getAssetChannels,
  getAssetUsageRightsStatus,
  isAssetExpired,
  USAGE_RIGHTS_STATUS_OPTIONS,
} from '../../constants/taxonomy';
import { EMPTY_VOCABULARY } from '../../vocabulary/useVocabulary';

/**
 * The values present, in vocabulary order, with anything the vocabulary does
 * not list (a legacy token, or the vocabulary not loaded yet) appended
 * alphabetically so no asset's value is ever unselectable.
 *
 * `key` maps a vocabulary value onto the form the `present` set holds, so a
 * caller that normalised its values (tier: `tier_1_5` -> `tier15`) compares
 * like with like on both passes. Without it the second pass would never find
 * the normalised token among the raw values and append every tier twice.
 */
export function orderByVocabulary(
  present: Set<string>,
  options: { value: string }[],
  key: (optionValue: string) => string = (v) => v,
): string[] {
  const ordered = options.map((opt) => opt.value).filter((v) => present.has(key(v)));
  const known = new Set(ordered.map(key));
  const extra = [...present].filter((v) => !known.has(v)).sort();
  return [...ordered, ...extra];
}

export interface AssetFilterState {
  /** Comma-separated; an asset matches when its list holds any of them. */
  nameplate: string;
  carline_code: string;
  business_unit: string;
  campaign: string;
  tier: string;
  channel: string;
  /** Comma-separated kinds (`constants/assetTypes.ts`). */
  asset_type: string;
  /** Comma-separated film lengths in seconds (`film_durations`). */
  duration: string;
  /** Comma-separated film sizes (`film_format_sizes`): a film's declared `format_size`. */
  aspect_ratio: string;
  usage_rights_status: string;
  reference_id: string;
}

export const DEFAULT_ASSET_FILTERS: AssetFilterState = {
  nameplate: '',
  carline_code: '',
  business_unit: '',
  campaign: '',
  tier: '',
  channel: '',
  asset_type: '',
  duration: '',
  aspect_ratio: '',
  usage_rights_status: '',
  reference_id: '',
};

export type AssetFilterKey = keyof AssetFilterState;

export const ASSET_FILTER_KEYS: AssetFilterKey[] = [
  'nameplate',
  'carline_code',
  'business_unit',
  'campaign',
  'tier',
  'channel',
  'asset_type',
  'duration',
  'aspect_ratio',
  'usage_rights_status',
  'reference_id',
];

// Bumped when the `format` facet became `asset_type`, and again when duration
// ('15s' -> '15') and aspect ratio ('16:9' -> '16x9') changed their values, so
// a persisted selection from before does not linger as one nothing matches.
const STORAGE_KEY = 'al-filters-v3';

/**
 * Tiers compare across two vocabularies: assets store `tier_1`-style tokens,
 * while content-tracker briefing rows carry the media plan's label, almost
 * always with a description in brackets ("Tier 2 (Lead Gen)", "Tier 1.5
 * (Consideration)"). The bracketed part is dropped, then case and separators,
 * so "tier_2", "Tier 2" and "Tier 2 (Lead Gen)" are the same tier without
 * maintaining a mapping between the two systems.
 *
 * The one definition for both row matching and the tier filter. Before the
 * bracket was dropped, "Tier 2 (Lead Gen)" became "tier2leadgen" and matched
 * no asset at all, so almost no briefing row had an eligible asset.
 */
export function normaliseTier(tier: string | null | undefined): string {
  return (tier ?? '')
    .replace(/\(.*?\)/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Whether the asset is tagged with the briefing row's nameplate.
 *
 * List membership, case-insensitive: a master tagged Kuga-then-Puma can be
 * shopped for a Puma row. Shared by the campaign filter and the Published
 * tab's eligibility gate so the two can't drift apart.
 */
export function hasNameplate(asset: AssetResponse, nameplate: string | null | undefined): boolean {
  if (!nameplate) return false;
  const wanted = nameplate.trim().toLowerCase();
  return (asset.nameplate ?? []).some((entry) => entry.trim().toLowerCase() === wanted);
}

/**
 * Whether an asset can be briefed against a media-plan row, expiry aside.
 *
 * Expiry is the caller's: eligibility drops expired assets, while the
 * "matches exist but have expired" notice has to count them.
 */
export function matchesBriefingRow(
  asset: AssetResponse,
  row: BriefingRow,
  formatTypes: readonly FormatTypeOption[],
): boolean {
  if (asset.asset_type === 'master_image') return masterMatchesBriefingRow(asset, row, formatTypes);
  // Not yet briefable: the rows fetched are Meta image placements, which a
  // film cannot fill, and a film carries no platform rights to match on.
  if (asset.asset_type === 'filmic') return false;
  // Everything else is matched on the platforms it is cleared for.
  const rowPlatform = row.platform?.toLowerCase() ?? '';
  return (
    (asset.platform_rights ?? []).some((right) => right.toLowerCase() === rowPlatform) &&
    hasNameplate(asset, row.nameplate) &&
    matchesRowTier(asset, row)
  );
}

/**
 * A master image is matched on its format and its licence rather than on
 * platforms, which it does not have: the format says where it runs. Domain
 * plays no part, since a row carries none. Which format type a row takes, and
 * whether its size counts, is the vocabulary's (`format_types`).
 */
export function masterMatchesBriefingRow(
  asset: AssetResponse,
  row: BriefingRow,
  formatTypes: readonly FormatTypeOption[],
): boolean {
  const formatType = formatTypeForRow(row, formatTypes);
  return (
    hasNameplate(asset, row.nameplate) &&
    matchesRowTier(asset, row) &&
    (asset.channel_rights ?? []).includes(BRIEFED_CHANNEL_RIGHT) &&
    coversMarket(asset, row.market) &&
    formatType !== undefined &&
    asset.format_type === formatType.value &&
    (!formatType.briefed_by_size || matchesRowSize(asset.format_size, row.size))
  );
}

// Every media-plan row is a digital buy.
const BRIEFED_CHANNEL_RIGHT = 'digital';

/** The format type cut for a media-plan row, if any; none for a format with no still. */
function formatTypeForRow(
  row: BriefingRow,
  formatTypes: readonly FormatTypeOption[],
): FormatTypeOption | undefined {
  const format = row.format?.trim().toLowerCase() ?? '';
  const platform = row.platform?.trim().toLowerCase() ?? '';
  return formatTypes.find((type) =>
    type.media_plan_rows.some(
      (planRow) =>
        planRow.format === format && (planRow.platform === null || planRow.platform === platform),
    ),
  );
}

/**
 * Whether a media-plan row carries a tier. Every row should: one without is
 * bad data in the media plan, matches no asset, and is reported as an error
 * rather than read as "any tier".
 */
export function rowHasTier(row: BriefingRow): boolean {
  return normaliseTier(row.tier) !== '';
}

function matchesRowTier(asset: AssetResponse, row: BriefingRow): boolean {
  if (!rowHasTier(row)) return false;
  return (asset.tier ?? []).some((t) => normaliseTier(t) === normaliseTier(row.tier));
}

// Content Tracker spells the UK as MAP does; the Asset Library uses ISO 3166.
const COUNTRY_BY_MARKET: Record<string, string> = { UK: 'GB' };

function coversMarket(asset: AssetResponse, market: string | null | undefined): boolean {
  const code = market?.trim().toUpperCase() ?? '';
  if (!code) return false;
  return asset.countries_covered.includes(COUNTRY_BY_MARKET[code] ?? code);
}

// A row's size and a master's count as the same within this relative margin,
// so pixel sizes rounded to whole pixels still land: 1200x628 is 1.91x1.
const RATIO_TOLERANCE = 0.01;

/**
 * Whether the master's size is among the row's. A row's `size` is MAP's free
 * text, usually a list of ratios ("1x1, 9x16", "4x5 & 1x1") and for search
 * rows pixel sizes ("Text, 1200x628, 1200x1200"), so both are compared as
 * width-to-height ratios. Anything that is not a size ("Text Ads") is skipped.
 */
export function matchesRowSize(
  formatSize: string | null,
  rowSize: string | null | undefined,
): boolean {
  const wanted = formatSize ? parseRatio(formatSize) : null;
  if (wanted === null) return false;
  return (rowSize ?? '')
    .split(/[,&/;]/)
    .map(parseRatio)
    .some((ratio) => ratio !== null && Math.abs(ratio - wanted) / wanted <= RATIO_TOLERANCE);
}

function parseRatio(size: string): number | null {
  const match = /^(\d+(?:\.\d+)?)\s*[x:×]\s*(\d+(?:\.\d+)?)/i.exec(size.trim());
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  return width > 0 && height > 0 ? width / height : null;
}

/**
 * The channels the Channel filter files an asset under.
 *
 * A master image runs in its format's channel, so the server's derived
 * `channel` is the answer; it has none until the format is whole. Anything
 * else keeps every channel its rights reach: the server's `channel` is null
 * when rights span two channels, and the filter still has to offer the asset
 * under both.
 */
export function getFilterChannels(asset: AssetResponse): string[] {
  if (asset.asset_type === 'master_image') return asset.channel ? [asset.channel] : [];
  return getAssetChannels(asset.platform_rights);
}

/**
 * Check if an asset matches a single filter key given the current filter value.
 *
 * `formatTypes` is the vocabulary's, for the campaign filter's masters;
 * without it no master matches a campaign.
 */
export function matchesFilterForKey(
  key: AssetFilterKey,
  filterVal: string,
  asset: AssetResponse,
  campaignsMap?: Map<string, BriefingRow>,
  formatTypes: readonly FormatTypeOption[] = [],
): boolean {
  if (!filterVal || filterVal.trim() === '') return true;

  switch (key) {
    // The three vehicle lists: any-of, case-insensitive.
    case 'nameplate':
    case 'carline_code':
    case 'business_unit': {
      const selected = filterVal.split(',').map((s) => s.trim().toLowerCase());
      return (asset[key] ?? []).some((entry) => selected.includes(entry.toLowerCase()));
    }

    case 'campaign': {
      if (!campaignsMap || campaignsMap.size === 0) return false;
      const selectedCampaignIds = filterVal
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (selectedCampaignIds.length === 0) return true;
      // An asset matches if it satisfies ANY of the selected campaigns' requirements
      return selectedCampaignIds.some((campId) => {
        const row = campaignsMap.get(campId);
        if (!row) return false;
        // A master uses the basket's rule, expiry included, and so is strict
        // where the lenient matching below is not: a row with no tier matches
        // no master. Deliberately only masters for now; the other kinds keep
        // the filter's older behaviour, expired assets included.
        if (asset.asset_type === 'master_image') {
          return !isAssetExpired(asset) && masterMatchesBriefingRow(asset, row, formatTypes);
        }
        // Match nameplate strictly if required by campaign: any of the
        // asset's nameplates will do.
        if (row.nameplate && !hasNameplate(asset, row.nameplate)) {
          return false;
        }
        // Match tier strictly if required by campaign
        if (row.tier) {
          const rowTier = normaliseTier(row.tier);
          if (!(asset.tier ?? []).some((t) => normaliseTier(t) === rowTier)) {
            return false;
          }
        }
        // Match platform rights strictly if required by campaign
        if (row.platform) {
          if (!asset.platform_rights || asset.platform_rights.length === 0) {
            return false;
          }
          const rowPlatform = row.platform.toLowerCase();
          const hasPlatform = asset.platform_rights.some((p) => p.toLowerCase() === rowPlatform);
          if (!hasPlatform) return false;
        }
        return true;
      });
    }

    case 'tier': {
      const selectedTiers = filterVal.split(',').map((t) => normaliseTier(t.trim()));
      return (asset.tier ?? []).some((t) => selectedTiers.includes(normaliseTier(t)));
    }

    case 'channel': {
      const selectedChannels = filterVal.split(',').map((c) => c.trim().toLowerCase());
      return getFilterChannels(asset).some((ch) => selectedChannels.includes(ch));
    }

    case 'asset_type': {
      const selected = filterVal.split(',').map((t) => t.trim());
      return selected.includes(asset.asset_type);
    }

    // Both are a film's declared values, so only films match, as the API's
    // predicates gate on the kind the same way.
    case 'duration': {
      if (asset.asset_type !== 'filmic' || asset.duration_seconds === null) return false;
      const selected = filterVal.split(',').map((d) => d.trim());
      return selected.includes(String(asset.duration_seconds));
    }

    case 'aspect_ratio': {
      if (asset.asset_type !== 'filmic' || !asset.format_size) return false;
      const selected = filterVal.split(',').map((r) => r.trim());
      return selected.includes(asset.format_size);
    }

    case 'usage_rights_status': {
      const selectedStatuses = filterVal.split(',').map((s) => s.trim());
      const status = getAssetUsageRightsStatus(asset.expiry_rights);
      return status !== null && selectedStatuses.includes(status);
    }

    case 'reference_id':
      // Server-side filter; the page already sends it as `reference_id` and
      // the rows that come back are the answer. Re-checking them here can only
      // lose rows: the server also returns everything *derived from* a key
      // visual that matched (by code, file name or campaign), and a derivative
      // carries none of the hero's strings, so a local substring test threw the
      // masters away and left the hero looking unlinked.
      return true;

    default:
      return true;
  }
}

/**
 * Check if an asset matches all active filters.
 */
export function matchesAllFilters(
  asset: AssetResponse,
  filters: AssetFilterState,
  campaignsMap?: Map<string, BriefingRow>,
  formatTypes: readonly FormatTypeOption[] = [],
): boolean {
  for (const key of ASSET_FILTER_KEYS) {
    const val = filters[key];
    if (val && !matchesFilterForKey(key, val, asset, campaignsMap, formatTypes)) {
      return false;
    }
  }
  return true;
}

export function getActiveAssetFilterCount(filters: AssetFilterState): number {
  return ASSET_FILTER_KEYS.filter((key) => {
    const val = filters[key];
    return typeof val === 'string' && val.trim().length > 0;
  }).length;
}

export interface AssetFilterStateHook {
  filters: AssetFilterState;
  setFilters: React.Dispatch<React.SetStateAction<AssetFilterState>>;
  handleFilterChange: (newFilters: AssetFilterState) => void;
  handleClearFilters: () => void;
}

export function useAssetFilterState(): AssetFilterStateHook {
  const [filters, setFilters] = useState<AssetFilterState>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_ASSET_FILTERS, ...JSON.parse(saved) };
      }
    } catch {
      // Ignore storage error
    }
    return DEFAULT_ASSET_FILTERS;
  });

  // Persist to sessionStorage on changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Ignore storage error
    }
  }, [filters]);

  const handleFilterChange = useCallback((newFilters: AssetFilterState) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_ASSET_FILTERS);
  }, []);

  return {
    filters,
    setFilters,
    handleFilterChange,
    handleClearFilters,
  };
}

export function useAssetFilters(
  allAssets: AssetResponse[],
  briefingRows: BriefingRow[] = [],
  filterState?: AssetFilterStateHook,
  // Orders the nameplate and tier option lists. Optional so the hook stays
  // usable (and testable) without a query client; without it the options are
  // the values present, alphabetically.
  vocabulary: VocabularyResponse = EMPTY_VOCABULARY,
) {
  const internalState = useAssetFilterState();
  const { filters, setFilters, handleFilterChange, handleClearFilters } =
    filterState ?? internalState;

  const campaignsMap = useMemo(() => {
    const map = new Map<string, BriefingRow>();
    for (const row of briefingRows) {
      map.set(row.id, row);
      if (row.campaign_id) {
        map.set(row.campaign_id, row);
      }
    }
    return map;
  }, [briefingRows]);

  const activeFilterCount = useMemo(() => getActiveAssetFilterCount(filters), [filters]);
  const isFiltered = activeFilterCount > 0;

  // Filtered asset list
  const filteredAssets = useMemo(() => {
    if (!isFiltered) return allAssets;
    return allAssets.filter((asset) =>
      matchesAllFilters(asset, filters, campaignsMap, vocabulary.format_types),
    );
  }, [allAssets, filters, isFiltered, campaignsMap, vocabulary.format_types]);

  // Dynamic available options computation
  const availableOptions = useMemo(() => {
    const computeOptions = (targetKey: AssetFilterKey): string[] => {
      // Filter assets by all active filters EXCEPT the targetKey
      const surviving = allAssets.filter((asset) => {
        for (const key of ASSET_FILTER_KEYS) {
          if (key === targetKey || !filters[key]) continue;
          if (
            !matchesFilterForKey(key, filters[key], asset, campaignsMap, vocabulary.format_types)
          ) {
            return false;
          }
        }
        return true;
      });

      switch (targetKey) {
        case 'nameplate': {
          const present = new Set(surviving.flatMap((a) => a.nameplate ?? []).filter(Boolean));
          return orderByVocabulary(present, vocabulary.nameplates);
        }

        case 'carline_code': {
          const present = new Set(surviving.flatMap((a) => a.carline_code ?? []).filter(Boolean));
          return orderByVocabulary(present, vocabulary.carline_codes);
        }

        case 'business_unit': {
          const present = new Set(
            surviving.flatMap((a) => a.business_unit ?? []).filter(Boolean),
          );
          return orderByVocabulary(present, vocabulary.business_units);
        }

        case 'tier': {
          const present = new Set(
            surviving.flatMap((a) => (a.tier ?? []).map(normaliseTier)).filter(Boolean),
          );
          return orderByVocabulary(present, vocabulary.tiers, normaliseTier);
        }

        case 'channel': {
          const present = new Set(surviving.flatMap(getFilterChannels));
          return CHANNEL_OPTIONS.map((opt) => opt.value).filter((ch) => present.has(ch));
        }

        case 'asset_type':
          return assetTypesOf(surviving);

        // Films only, like the filters themselves.
        case 'duration': {
          const present = new Set(
            surviving
              .filter((a) => a.asset_type === 'filmic' && a.duration_seconds !== null)
              .map((a) => String(a.duration_seconds)),
          );
          return vocabulary.film_durations.map(String).filter((d) => present.has(d));
        }

        case 'aspect_ratio': {
          const present = new Set(
            surviving.flatMap((a) =>
              a.asset_type === 'filmic' && a.format_size ? [a.format_size] : [],
            ),
          );
          return orderByVocabulary(present, vocabulary.film_format_sizes);
        }

        case 'usage_rights_status': {
          const presentStatuses = new Set<string>();
          for (const a of surviving) {
            const s = getAssetUsageRightsStatus(a.expiry_rights);
            if (s) presentStatuses.add(s);
          }
          return USAGE_RIGHTS_STATUS_OPTIONS.map((o) => o.value).filter((v) =>
            presentStatuses.has(v),
          );
        }

        case 'campaign': {
          // List of campaigns that match surviving assets
          return briefingRows.map((r) => r.id);
        }

        default:
          return [];
      }
    };

    return {
      nameplate: computeOptions('nameplate'),
      carline_code: computeOptions('carline_code'),
      business_unit: computeOptions('business_unit'),
      tier: computeOptions('tier'),
      channel: computeOptions('channel'),
      asset_type: computeOptions('asset_type'),
      duration: computeOptions('duration'),
      aspect_ratio: computeOptions('aspect_ratio'),
      usage_rights_status: computeOptions('usage_rights_status'),
      campaign: computeOptions('campaign'),
      reference_id: [],
    };
  }, [allAssets, filters, campaignsMap, briefingRows, vocabulary]);

  return {
    filters,
    filteredAssets,
    totalAssets: allAssets.length,
    filteredCount: filteredAssets.length,
    isFiltered,
    activeFilterCount,
    availableOptions,
    setFilters,
    handleFilterChange,
    handleClearFilters,
  };
}
